import React from "react";
import { Box, Paper, Skeleton } from "@mui/material";

interface SkeletonLoaderProps {
  variant: "clientOverview" | "list" | "detail" | "card";
  rows?: number;
  sections?: number;
}

/* skeletonColorSx depends on if we are using a wave animation - current state before Review is animation="wave" */
const skeletonColorSx = {
  "&::after": {
    background:
      "linear-gradient(90deg, transparent, rgba(93, 157, 220, 0.09), transparent)",
  },
};

const SkeletonCard: React.FC<{ height?: number }> = ({ height = 160 }) => (
  <Paper sx={{ p: 2.5, borderRadius: 2 }}>
    <Skeleton
      variant="text"
      width="40%"
      height={24}
      animation="wave"
      sx={{ mb: 2, ...skeletonColorSx }}
    />
    <Skeleton
      variant="rectangular"
      height={height - 60}
      animation="wave"
      sx={{ borderRadius: 1, ...skeletonColorSx }}
    />
  </Paper>
);

const SkeletonTextSection: React.FC = () => (
  <Paper sx={{ p: 2.5, borderRadius: 2 }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
      <Skeleton
        variant="circular"
        width={20}
        height={20}
        animation="wave"
        sx={skeletonColorSx}
      />
      <Skeleton
        variant="text"
        width="30%"
        height={24}
        animation="wave"
        sx={skeletonColorSx}
      />
    </Box>
    <Skeleton
      variant="rectangular"
      height={56}
      animation="wave"
      sx={{ borderRadius: 1, mb: 2, ...skeletonColorSx }}
    />
    <Skeleton
      variant="text"
      width="15%"
      height={20}
      animation="wave"
      sx={{ mb: 1, ...skeletonColorSx }}
    />
    <Skeleton
      variant="text"
      width="100%"
      height={16}
      animation="wave"
      sx={skeletonColorSx}
    />
    <Skeleton
      variant="text"
      width="80%"
      height={16}
      animation="wave"
      sx={skeletonColorSx}
    />
  </Paper>
);

const ClientOverviewSkeleton: React.FC = () => (
  <>
    {/* Action bar */}
    <Paper
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 3,
        py: 2,
        borderRadius: 2,
        boxShadow: 1,
      }}
    >
      <Skeleton
        variant="circular"
        width={32}
        height={32}
        animation="wave"
        sx={skeletonColorSx}
      />
      <Skeleton
        variant="text"
        width="20%"
        height={32}
        animation="wave"
        sx={skeletonColorSx}
      />
      <Box sx={{ display: "flex", gap: 1 }}>
        <Skeleton
          variant="rectangular"
          width={80}
          height={32}
          animation="wave"
          sx={{ borderRadius: 1, ...skeletonColorSx }}
        />
        <Skeleton
          variant="rectangular"
          width={80}
          height={32}
          animation="wave"
          sx={{ borderRadius: 1, ...skeletonColorSx }}
        />
      </Box>
    </Paper>

    {/* Key Information */}
    <Paper sx={{ p: 2.5, borderRadius: 2 }}>
      <Skeleton
        variant="text"
        width="25%"
        height={24}
        animation="wave"
        sx={{ mb: 2, ...skeletonColorSx }}
      />
      <Box sx={{ display: "flex", gap: 2.5 }}>
        <Skeleton
          variant="circular"
          width={80}
          height={80}
          animation="wave"
          sx={skeletonColorSx}
        />
        <Box
          sx={{
            flex: 1,
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: 2,
          }}
        >
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton
              key={i}
              variant="rectangular"
              height={40}
              animation="wave"
              sx={{ borderRadius: 2, ...skeletonColorSx }}
            />
          ))}
        </Box>
      </Box>
    </Paper>

    {/* Client Objectives */}
    <SkeletonTextSection />

    {/* Further Remarks */}
    <SkeletonTextSection />

    {/* Methods & Personal Info */}
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        gap: 2,
      }}
    >
      <SkeletonCard height={180} />
      <SkeletonCard height={180} />
    </Box>

    {/* Coaching Packages & Invoices */}
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        gap: 2,
      }}
    >
      <SkeletonCard height={200} />
      <SkeletonCard height={200} />
    </Box>
  </>
);

const ListSkeleton: React.FC<{ rows?: number }> = ({ rows = 5 }) => (
  <Paper sx={{ p: 2.5, borderRadius: 2 }}>
    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
      <Skeleton
        variant="text"
        width="30%"
        height={32}
        animation="wave"
        sx={skeletonColorSx}
      />
      <Skeleton
        variant="rectangular"
        width={120}
        height={36}
        animation="wave"
        sx={{ borderRadius: 1, ...skeletonColorSx }}
      />
    </Box>
    {Array.from({ length: rows }).map((_, i) => (
      <Skeleton
        key={i}
        variant="rectangular"
        height={48}
        animation="wave"
        sx={{ borderRadius: 1, mb: 1, ...skeletonColorSx }}
      />
    ))}
  </Paper>
);

const DetailSkeleton: React.FC<{ sections?: number }> = ({ sections = 3 }) => (
  <>
    <Paper
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 3,
        py: 2,
        borderRadius: 2,
        boxShadow: 1,
      }}
    >
      <Skeleton
        variant="circular"
        width={32}
        height={32}
        animation="wave"
        sx={skeletonColorSx}
      />
      <Skeleton
        variant="text"
        width="25%"
        height={32}
        animation="wave"
        sx={skeletonColorSx}
      />
      <Skeleton
        variant="rectangular"
        width={80}
        height={32}
        animation="wave"
        sx={{ borderRadius: 1, ...skeletonColorSx }}
      />
    </Paper>
    {Array.from({ length: sections }).map((_, i) => (
      <SkeletonCard key={i} height={160} />
    ))}
  </>
);

const SkeletonLoader: React.FC<SkeletonLoaderProps> = ({
  variant,
  rows,
  sections,
}) => {
  switch (variant) {
    case "clientOverview":
      return <ClientOverviewSkeleton />;
    case "list":
      return <ListSkeleton rows={rows} />;
    case "detail":
      return <DetailSkeleton sections={sections} />;
    case "card":
      return <SkeletonCard />;
    default:
      return <SkeletonCard />;
  }
};

export default SkeletonLoader;
